import type { AuraCut, AuraFrame } from "@nano/content";

/** How far the soft glow reaches beyond the solid shape, in source pixels. */
const FEATHER = 4;

/**
 * Cuts one particle (a heart, a sparkle, a snowflake) out of an aura's
 * painted sprite. A rectangle around it would also catch slices of the
 * neighbouring trail and glow, which show as hard square edges once the
 * particle moves on its own; instead this keeps only the solid shape that
 * holds the frame's centre, plus a feathered rim of its own glow.
 */
export function cutParticle(source: CanvasImageSource, frame: AuraFrame, cut: AuraCut): HTMLCanvasElement {
  const { w, h } = frame;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return canvas;
  ctx.drawImage(source, frame.x, frame.y, w, h, 0, 0, w, h);
  const image = ctx.getImageData(0, 0, w, h);
  const alpha = (i: number) => image.data[i * 4 + 3] as number;

  // Seed: the frame centre (frames are centred on their particle), or failing that
  // the most opaque pixel in the middle third.
  let seed = Math.floor(h / 2) * w + Math.floor(w / 2);
  if (alpha(seed) < cut.solid) {
    for (let y = Math.floor(h / 3); y < Math.ceil((h * 2) / 3); y += 1) {
      for (let x = Math.floor(w / 3); x < Math.ceil((w * 2) / 3); x += 1) {
        const i = y * w + x;
        if (alpha(i) > alpha(seed)) seed = i;
      }
    }
  }
  if (seed < 0) return canvas;
  const threshold = Math.min(cut.solid, alpha(seed));
  const rgb = (i: number): [number, number, number] => [image.data[i * 4] as number, image.data[i * 4 + 1] as number, image.data[i * 4 + 2] as number];
  const [sr, sg, sb] = rgb(seed);
  // Where the particle touches a band of other colours (a cloud on the rainbow), colour decides.
  const similar = (i: number) => {
    if (cut.color === undefined) return true;
    const [r, g, b] = rgb(i);
    return Math.hypot(r - sr, g - sg, b - sb) <= cut.color;
  };

  // Flood the solid shape from the seed.
  const inside = new Uint8Array(w * h);
  const stack = [seed];
  inside[seed] = 1;
  while (stack.length > 0) {
    const i = stack.pop() as number;
    const x = i % w;
    const y = (i - x) / w;
    for (const [nx, ny] of [
      [x + 1, y],
      [x - 1, y],
      [x, y + 1],
      [x, y - 1],
    ] as const) {
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const n = ny * w + nx;
      if (!inside[n] && alpha(n) >= threshold && similar(n)) {
        inside[n] = 1;
        stack.push(n);
      }
    }
  }

  // Distance (in pixels, chessboard) from the shape, out to FEATHER: a soft rim.
  const distance = new Uint8Array(w * h).fill(255);
  let ring: number[] = [];
  for (let i = 0; i < w * h; i += 1) {
    if (inside[i]) {
      distance[i] = 0;
      ring.push(i);
    }
  }
  for (let d = 1; d <= FEATHER && ring.length > 0; d += 1) {
    const next: number[] = [];
    for (const i of ring) {
      const x = i % w;
      const y = (i - x) / w;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const n = ny * w + nx;
          if ((distance[n] as number) > d) {
            distance[n] = d;
            next.push(n);
          }
        }
      }
    }
    ring = next;
  }

  // Keep the shape, fade its own glow over the rim, drop everything else; and
  // fade the last pixels at the frame edge so no straight cut can show.
  for (let i = 0; i < w * h; i += 1) {
    const x = i % w;
    const y = (i - x) / w;
    const d = distance[i] as number;
    const keep = d === 0 ? 1 : d <= FEATHER ? 1 - d / (FEATHER + 1) : 0;
    const edge = Math.min(1, (Math.min(x, y, w - 1 - x, h - 1 - y) + 1) / 3);
    // Round particles also drop whatever painted shape reaches into the frame's corners.
    const r = Math.hypot((x + 0.5) / w - 0.5, (y + 0.5) / h - 0.5) * 2;
    const round = cut.round ? Math.min(1, Math.max(0, (1.08 - r) / 0.12)) : 1;
    image.data[i * 4 + 3] = Math.round(alpha(i) * keep * edge * round);
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}
